import { z } from "zod";

// Byte-for-byte copy: Polaris packages/polaris-shared/src/sityweb-economics.ts.
// T-PL-24. Version additive; amounts are integer minor units, never floats.
const id = z.string().min(1).max(200);
const instant = z.string().datetime({ offset: true });
const cents = z.number().int().nonnegative().safe();
const currency = z.string().regex(/^[A-Z]{3}$/u);
const coverageState = z.enum(["complete", "partial", "unavailable"]);

export const sitywebEconomicsQueryRequestSchema = z
  .object({
    workspaceId: z.string().uuid(),
    siteIds: z.array(id).min(1).max(200),
    cursor: z.string().min(1).max(4096).optional(),
    limit: z.number().int().min(1).max(100).default(50),
  })
  .strict();

export const sitywebEconomicCustomerSchema = z
  .object({ id, siteIds: z.array(id).max(200), ownerOrgIds: z.array(id).max(200) })
  .strict();

export const sitywebEconomicPaymentSchema = z
  .object({
    id,
    customerId: id,
    siteIds: z.array(id).min(1).max(200),
    subscriptionId: id.nullable(),
    invoiceId: id.nullable(),
    kind: z.enum(["subscription", "credit_pack", "other"]),
    source: z.enum(["stripe", "bank_transfer"]),
    live: z.boolean(),
    currency,
    // New settled cash only. Applied credits/customer balance are not receipts.
    amountCents: cents,
    creditAppliedCents: cents,
    taxCents: cents.nullable(),
    netAmountCents: cents.nullable(),
    taxAllocation: z.enum(["receipt", "proportional_invoice", "unknown"]),
    paidAt: instant,
    updatedAt: instant,
  })
  .strict();

export const sitywebEconomicRefundSchema = z
  .object({
    id,
    paymentId: id,
    customerId: id,
    currency,
    amountCents: cents,
    taxCents: cents.nullable(),
    netAmountCents: cents.nullable(),
    refundedAt: instant,
    updatedAt: instant,
  })
  .strict();

export const sitywebEconomicSubscriptionSchema = z
  .object({
    id,
    customerId: id,
    siteId: id,
    provider: z.enum(["stripe", "manual"]),
    status: z.string().min(1).max(100),
    // Verified paid activation: max(actual source start, this subscription's first settled cash).
    // Null without proof; the payer's earlier payment on another site never ends this trial.
    startedAt: instant.nullable(),
    endedAt: instant.nullable(),
    cancelAtPeriodEnd: z.boolean(),
    currency: currency.nullable(),
    recurringAmountCents: cents.nullable(),
    interval: z.enum(["day", "week", "month", "year"]).nullable(),
    intervalCount: z.number().int().positive().nullable(),
    updatedAt: instant,
  })
  .strict();

export const sitywebEconomicsQueryResponseSchema = z
  .object({
    version: z.literal(1),
    snapshotAt: instant,
    customers: z.array(sitywebEconomicCustomerSchema).max(200),
    payments: z.array(sitywebEconomicPaymentSchema).max(1000),
    refunds: z.array(sitywebEconomicRefundSchema).max(1000),
    subscriptions: z.array(sitywebEconomicSubscriptionSchema).max(100),
    coverage: z
      .object({
        payments: coverageState,
        refunds: coverageState,
        subscriptionHistory: coverageState,
        prices: coverageState,
        reasons: z.array(z.string().min(1).max(200)).max(100),
        authorizedSiteIds: z.array(id).max(200),
        rejectedSiteIds: z.array(id).max(200),
        from: instant.nullable(),
        through: instant,
      })
      .strict(),
    nextCursor: z.string().min(1).max(4096).nullable(),
  })
  .strict();

export type SitywebEconomicsQueryRequest = z.infer<typeof sitywebEconomicsQueryRequestSchema>;
export type SitywebEconomicsQueryResponse = z.infer<typeof sitywebEconomicsQueryResponseSchema>;
export type SitywebEconomicPayment = z.infer<typeof sitywebEconomicPaymentSchema>;
export type SitywebEconomicRefund = z.infer<typeof sitywebEconomicRefundSchema>;
export type SitywebEconomicSubscription = z.infer<typeof sitywebEconomicSubscriptionSchema>;

// ADR 0078: direct site billing uses an explicit new identity namespace.
export const sitywebEconomicsV2QueryResponseSchema = sitywebEconomicsQueryResponseSchema.extend({
  version: z.literal(2),
  customers: z.array(sitywebEconomicCustomerSchema.omit({ ownerOrgIds: true })).max(200),
});
export type SitywebEconomicsV2QueryResponse = z.infer<typeof sitywebEconomicsV2QueryResponseSchema>;

// T-PL-40: v2 plus, per subscription, whether its own paid service is proven continuous. The
// workspace-wide `subscriptionHistory` stays as it is; payment gaps still apply to everyone.
export const sitywebEconomicSubscriptionV3Schema = sitywebEconomicSubscriptionSchema.extend({
  historyVerified: z.boolean(),
});
export const sitywebEconomicsV3QueryResponseSchema = sitywebEconomicsV2QueryResponseSchema.extend({
  version: z.literal(3),
  subscriptions: z.array(sitywebEconomicSubscriptionV3Schema).max(100),
});
export type SitywebEconomicSubscriptionV3 = z.infer<typeof sitywebEconomicSubscriptionV3Schema>;
export type SitywebEconomicsV3QueryResponse = z.infer<typeof sitywebEconomicsV3QueryResponseSchema>;
